import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiSearch, FiTrash2, FiUpload } from 'react-icons/fi';
import {
  addPreRegistered,
  bulkAddPreRegistered,
  deletePreRegistered,
  getPreRegistered,
} from '../services/api';

interface PreRegisteredUser {
  preRegId: number;
  fullName: string;
  mobile: string;
  pinCode?: string;
  userType: 'dealer' | 'contractor';
  isActive: boolean;
  linkedUserId?: number | null;
  linkedName?: string;
  linkedVerified?: boolean | null;
  createdAt: string;
}

const pageSize = 20;

export default function PreRegisteredUsers() {
  const [users, setUsers] = useState<PreRegisteredUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [saving, setSaving] = useState(false);

  const [newUser, setNewUser] = useState({
    fullName: '',
    mobile: '',
    pinCode: '',
    userType: 'dealer' as 'dealer' | 'contractor',
  });

  const loadUsers = useCallback(async (searchValue = search) => {
    try {
      setLoading(true);
      const res = await getPreRegistered({
        page,
        pageSize,
        search: searchValue,
        userType,
      });
      setUsers(res.data.data ?? []);
      setTotalCount(res.data.totalCount ?? 0);
    } catch (error) {
      console.error('Failed to load pre-registered users:', error);
      toast.error('Unable to load pre-registered users');
    } finally {
      setLoading(false);
    }
  }, [page, search, userType]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = () => {
    setPage(1);
    loadUsers(search);
  };

  const handleAdd = async () => {
    if (!newUser.fullName.trim() || newUser.mobile.trim().length !== 10) {
      toast.error('Name and valid 10-digit mobile number are required');
      return;
    }

    try {
      setSaving(true);
      await addPreRegistered({
        fullName: newUser.fullName.trim(),
        mobile: newUser.mobile.trim(),
        pinCode: newUser.pinCode.trim() || null,
        userType: newUser.userType,
      });
      toast.success('Pre-registered user added');
      setShowAddModal(false);
      setNewUser({ fullName: '', mobile: '', pinCode: '', userType: 'dealer' });
      loadUsers();
    } catch (error: any) {
      console.error('Failed to add user:', error);
      toast.error(error.response?.data?.message || 'Unable to add pre-registered user');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkAdd = async () => {
    const lines = bulkText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      toast.error('Add at least one CSV row before importing');
      return;
    }

    const items = lines
      .map((line) => {
        const [fullName = '', mobile = '', pinCode = '', userTypeValue = 'dealer'] = line
          .split(',')
          .map((part) => part.trim());

        return {
          fullName,
          mobile,
          pinCode: pinCode || null,
          userType: userTypeValue.toLowerCase() === 'contractor' ? 'contractor' : 'dealer',
        };
      })
      .filter((item) => item.fullName && item.mobile);

    if (items.length === 0) {
      toast.error('No valid CSV rows found');
      return;
    }

    try {
      setSaving(true);
      const res = await bulkAddPreRegistered(items);
      toast.success(res.data?.message || 'Bulk import completed');
      setShowBulkModal(false);
      setBulkText('');
      loadUsers();
    } catch (error: any) {
      console.error('Failed to bulk add:', error);
      toast.error(error.response?.data?.message || 'Bulk import failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this pre-registered user?')) {
      return;
    }

    try {
      await deletePreRegistered(id);
      toast.success('Pre-registered user deleted');
      loadUsers();
    } catch (error: any) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.message || 'Unable to delete user');
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Pre-Registered Users</h1>
        <p>Manage the dealer and contractor master list used for instant redemption eligibility.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-filters">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <select
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
                setPage(1);
              }}
              className="filter-dropdown"
            >
              <option value="">All Types</option>
              <option value="dealer">Dealer</option>
              <option value="contractor">Contractor</option>
            </select>
            <button className="btn btn-outline" onClick={handleSearch}>
              <FiSearch /> Search
            </button>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => setShowBulkModal(true)}>
              <FiUpload /> Bulk Add
            </button>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              <FiPlus /> Add User
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Mobile</th>
                <th>PIN Code</th>
                <th>Type</th>
                <th>Status</th>
                <th>Linked Account</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center">Loading pre-registered users...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center">No pre-registered users found</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.preRegId}>
                    <td>{user.fullName}</td>
                    <td>{user.mobile}</td>
                    <td>{user.pinCode || '-'}</td>
                    <td>
                      <span className={`badge ${user.userType === 'dealer' ? 'badge-success' : 'badge-info'}`}>
                        {user.userType}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.isActive ? 'badge-success' : 'badge-secondary'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {user.linkedName ? (
                        <div className="stack-sm">
                          <span>{user.linkedName}</span>
                          <span className={`badge ${user.linkedVerified ? 'badge-success' : 'badge-warning'}`}>
                            {user.linkedVerified ? 'Verified' : 'Pending'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">Not linked yet</span>
                      )}
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(user.preRegId)}
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <span className="page-info">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Pre-Registered User</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="text"
                  value={newUser.mobile}
                  onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>PIN Code</label>
                <input
                  type="text"
                  value={newUser.pinCode}
                  onChange={(e) => setNewUser({ ...newUser, pinCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>User Type</label>
                <select
                  value={newUser.userType}
                  onChange={(e) => setNewUser({ ...newUser, userType: e.target.value as 'dealer' | 'contractor' })}
                  className="form-control"
                >
                  <option value="dealer">Dealer</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>
                <FiPlus /> Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Add Users</h3>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-muted">Use CSV format: Name, Mobile, PIN Code, Type (dealer or contractor).</p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                className="form-control"
                rows={10}
                placeholder="John Doe, 9876543210, 110001, dealer&#10;Jane Smith, 9876543211, 110002, contractor"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowBulkModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkAdd} disabled={saving}>
                <FiUpload /> Import Users
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
